/**
 * Availability — inspector capacity across a date range.
 *
 * Wears the Jobs Overview furniture (`Kpi` tiles, an `overview-band` filter
 * row, a banded grid) so it reads as the same product. The tiles are strict
 * aggregates of what the grid below already shows — no new measures were
 * invented to fill the row.
 */

import { useEffect, useMemo, useState } from 'react';
import type { AvailabilityForecast } from '@cer-autofill/shared';
import { DEFAULT_BUSINESS_HOURS } from '@cer-autofill/shared';
import {
  apiErrorReason,
  createAvailabilityBlock,
  deleteAvailabilityBlock,
  fetchAvailabilityForecast,
} from '../api.js';
import { getSession } from '../session.js';
import { AvailabilityDayEditor } from '../components/AvailabilityDayEditor.js';
import { Kpi } from '../components/Kpi.js';
import { FilterSelect } from '../components/FilterSelect.js';
import { percentage } from '../components/trend.js';
import { formatEventTime } from '../format.js';

function isoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function initialRange() {
  const from = new Date();
  const to = new Date(from);
  to.setDate(to.getDate() + 27);
  return { from: isoDate(from), to: isoDate(to) };
}

export function AvailabilityScreen() {
  const [range, setRange] = useState(initialRange);
  const [inspectorId, setInspectorId] = useState('');
  const [data, setData] = useState<AvailabilityForecast | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<{ inspectorId: string; date: string } | null>(null);
  const roles = getSession()?.roles ?? [];
  const ownInspectorId = getSession()?.inspectorId;
  /** Admins and schedulers edit anyone; an inspector edits only their own row. */
  const canManageOthers = roles.some((role) => role === 'admin' || role === 'support');

  useEffect(() => {
    setLoading(true);
    setError(null);
    void fetchAvailabilityForecast(range.from, range.to)
      .then(setData)
      .catch((err: unknown) => setError(apiErrorReason(err)))
      .finally(() => setLoading(false));
  }, [range, refreshKey]);

  function canEditInspector(candidateInspectorId: string): boolean {
    if (canManageOthers) return true;
    return Boolean(ownInspectorId) && candidateInspectorId === ownInspectorId;
  }

  async function addBlock(inspectorId: string, input: { start: string; end: string }) {
    setSaving(true);
    setError(null);
    try {
      await createAvailabilityBlock({
        ...(canManageOthers ? { inspectorId } : {}),
        start: input.start,
        end: input.end,
      });
      setRefreshKey((value) => value + 1);
    } catch (err) {
      setError(apiErrorReason(err));
    } finally {
      setSaving(false);
    }
  }

  async function removeBlock(blockId: string) {
    setSaving(true);
    setError(null);
    try {
      await deleteAvailabilityBlock(blockId);
      setRefreshKey((value) => value + 1);
    } catch (err) {
      setError(apiErrorReason(err));
    } finally {
      setSaving(false);
    }
  }

  const inspectors = useMemo(
    () => data?.inspectors.filter((item) => !inspectorId || item.inspectorId === inspectorId) ?? [],
    [data, inspectorId],
  );
  const dates = inspectors[0]?.days.map((day) => day.date) ?? [];

  /**
   * Resolved from the freshly-loaded forecast rather than captured at click
   * time, so the drawer reflects a save the moment the grid refreshes.
   */
  const editingContext = useMemo(() => {
    if (!editing) return null;
    const inspector = data?.inspectors.find((item) => item.inspectorId === editing.inspectorId);
    const day = inspector?.days.find((item) => item.date === editing.date);
    return inspector && day ? { inspector, day } : null;
  }, [editing, data]);

  const businessHours = useMemo(
    () => ({
      startMinutes: data?.assumptions.workdayStartMinutes ?? DEFAULT_BUSINESS_HOURS.startMinutes,
      endMinutes: data?.assumptions.workdayEndMinutes ?? DEFAULT_BUSINESS_HOURS.endMinutes,
      workingDays: data?.assumptions.workingDays ?? DEFAULT_BUSINESS_HOURS.workingDays,
    }),
    [data],
  );

  /**
   * Roll the grid up. Every figure here is a sum or count over the exact
   * `days` the table renders, so a tile can never disagree with the cells
   * beneath it — weekends and closures are excluded from capacity the same
   * way `tone()` excludes them from utilisation.
   */
  const totals = useMemo(() => {
    const days = inspectors.flatMap((inspector) => inspector.days);
    const bookable = days.filter((day) => day.status !== 'non-working' && day.status !== 'closed');
    return {
      inspectors: inspectors.length,
      freeHours: Math.round(bookable.reduce((sum, day) => sum + day.availableMinutes, 0) / 60),
      fullyBooked: bookable.filter((day) => day.utilisationPercent >= 100).length,
      unavailable: bookable.filter((day) => day.status === 'unavailable').length,
      bookableDays: bookable.length,
    };
  }, [inspectors]);

  return (
    <main className="screen availability-screen">
      <div className="screen__header">
        <div>
          <h2>Availability</h2>
          <p className="subtitle">
            Leading Edge inspector capacity, including redacted ACTNOW booking conflicts
          </p>
        </div>
      </div>
      {error && <p className="error">{error}</p>}

      <section className="overview-kpis overview-kpis--four" aria-label="Availability totals">
        <Kpi
          label="Inspectors"
          value={data ? totals.inspectors : undefined}
          unit={totals.inspectors === 1 ? 'Person' : 'People'}
          tone="blue"
          icon="users"
        />
        <Kpi
          label="Free Capacity"
          value={data ? totals.freeHours : undefined}
          unit="Hours"
          tone="green"
          icon="clock"
        />
        <Kpi
          label="Fully Booked"
          value={data ? totals.fullyBooked : undefined}
          unit="Days"
          tone={totals.fullyBooked ? 'amber' : 'green'}
          icon="calendar"
          percent={percentage(totals.fullyBooked, totals.bookableDays)}
          percentCaption="of weekdays"
        />
        <Kpi
          label="Unavailable"
          value={data ? totals.unavailable : undefined}
          unit="Days"
          tone="neutral"
          icon="minus"
          percent={percentage(totals.unavailable, totals.bookableDays)}
          percentCaption="of weekdays"
        />
      </section>
      <section className="overview-band overview-filters" aria-label="Availability filters">
        <div className="filter-grid filter-grid--availability">
          <label>
            <span>From</span>
            <input
              type="date"
              value={range.from}
              onChange={(e) => setRange((value) => ({ ...value, from: e.target.value }))}
            />
          </label>
          <label>
            <span>To</span>
            <input
              type="date"
              value={range.to}
              onChange={(e) => setRange((value) => ({ ...value, to: e.target.value }))}
            />
          </label>
          <FilterSelect
            label="Inspector"
            value={inspectorId}
            onChange={setInspectorId}
            options={(data?.inspectors ?? []).map((item) => [item.inspectorId, item.displayName])}
            allLabel="All inspectors"
          />
        </div>
        <div className="filter-actions">
          <button
            type="button"
            className="ghost-btn"
            onClick={() => {
              setRange(initialRange());
              setInspectorId('');
            }}
          >
            Clear
          </button>
        </div>
      </section>
      {data?.sources.flatMap((source) => [
        ...(source.status !== 'connected'
          ? [
              <p className="banner banner--warning" key={`${source.id}:bookings`}>
                <strong>{source.label} bookings:</strong>{' '}
                {source.message ?? 'This source has not been configured yet.'}
              </p>,
            ]
          : []),
        ...(source.leaveStatus !== 'connected'
          ? [
              <p className="banner banner--warning" key={`${source.id}:leave`}>
                <strong>{source.label} leave:</strong>{' '}
                {source.leaveMessage ?? 'This source has not been configured yet.'}
              </p>,
            ]
          : []),
      ])}
      {loading && !data ? <p className="loading">Calculating availability…</p> : null}
      {data && inspectors.length === 0 ? (
        <p className="empty">No active Inspector users match this view.</p>
      ) : null}
      {inspectors.length ? (
        <section className="overview-band">
          <div className="band-heading">
            <h3>Inspector availability</h3>
            <span>
              {inspectors.length} inspector{inspectors.length === 1 ? '' : 's'} · {dates.length} day
              {dates.length === 1 ? '' : 's'}
            </span>
          </div>
          <div className="availability-table-wrap">
            <div
              className="availability-table"
              style={{ gridTemplateColumns: `220px repeat(${dates.length}, 112px)` }}
              role="table"
            >
              <div className="availability-cell availability-cell--header availability-cell--sticky">
                Inspector
              </div>
              {dates.map((date) => (
                <div className="availability-cell availability-cell--header" key={date}>
                  <strong>
                    {new Date(`${date}T00:00:00`).toLocaleDateString(undefined, {
                      weekday: 'short',
                    })}
                  </strong>
                  <span>
                    {new Date(`${date}T00:00:00`).toLocaleDateString(undefined, {
                      day: 'numeric',
                      month: 'short',
                    })}
                  </span>
                </div>
              ))}
              {inspectors.flatMap((inspector) => [
                <div
                  className="availability-cell availability-cell--inspector availability-cell--sticky"
                  key={`${inspector.userId}:name`}
                >
                  <strong>{inspector.displayName}</strong>
                  <span>
                    {inspector.linkedSources.map(sourceLabel).join(' + ') || 'No ServiceM8 links'}
                  </span>
                </div>,
                ...inspector.days.map((day) => {
                  const editable = canEditInspector(inspector.inspectorId);
                  return (
                    <button
                      type="button"
                      className={`availability-cell availability-day availability-day--${tone(day)}${
                        editable ? ' availability-day--editable' : ''
                      }`}
                      key={`${inspector.userId}:${day.date}`}
                      aria-label={`${inspector.displayName}, ${day.date}: ${summary(day)}`}
                      title={
                        day.events
                          .map(
                            (event) =>
                              `${formatEventTime(event.start)}–${formatEventTime(event.end)} ${event.label}`,
                          )
                          .join('\n') ||
                        (editable ? 'Click to declare unavailable time' : undefined)
                      }
                      onClick={() =>
                        setEditing({ inspectorId: inspector.inspectorId, date: day.date })
                      }
                    >
                      <strong>
                        {day.status === 'open' ? `${hours(day.availableMinutes)} free` : '—'}
                      </strong>
                      <span>{summary(day)}</span>
                      {day.events.slice(0, 2).map((event, index) => (
                        <small
                          className={`availability-event availability-event--${event.kind === 'booking' ? event.source : event.kind}`}
                          key={index}
                        >
                          {event.label}
                        </small>
                      ))}
                      {day.events.length > 2 ? (
                        <small className="availability-event availability-event--more">
                          +{day.events.length - 2} more
                        </small>
                      ) : null}
                    </button>
                  );
                }),
              ])}
            </div>
          </div>
        </section>
      ) : null}
      {editingContext ? (
        <AvailabilityDayEditor
          inspector={editingContext.inspector}
          day={editingContext.day}
          hours={businessHours}
          canEdit={canEditInspector(editingContext.inspector.inspectorId)}
          saving={saving}
          error={error}
          onClose={() => setEditing(null)}
          onAdd={(input) => void addBlock(editingContext.inspector.inspectorId, input)}
          onRemove={(blockId) => void removeBlock(blockId)}
        />
      ) : null}
    </main>
  );
}

type Day = AvailabilityForecast['inspectors'][number]['days'][number];

function sourceLabel(source: 'leadingEdge' | 'actnow') {
  return source === 'actnow' ? 'ACTNOW' : 'Leading Edge';
}
function tone(day: Day) {
  if (day.status === 'non-working' || day.status === 'closed') return 'closed';
  if (day.status === 'unavailable') return 'unavailable';
  if (day.utilisationPercent >= 100) return 'full';
  if (day.utilisationPercent >= 75) return 'limited';
  return 'available';
}
/**
 * The second line carries whichever fact matters most for that day — a
 * scheduler scanning the grid needs "why can't I book this" before "how busy".
 */
function summary(day: Day) {
  if (day.status === 'non-working') {
    // Distinguish "the business is shut" from "this person doesn't work today".
    return [0, 6].includes(new Date(`${day.date}T00:00:00`).getDay())
      ? 'Weekend'
      : 'Non-working day';
  }
  if (day.status === 'closed') return 'Business closed';
  if (day.status === 'unavailable') return 'Unavailable';
  const parts: string[] = [];
  if (day.bookedMinutes) parts.push(`${hours(day.bookedMinutes)} booked`);
  if (day.unavailableMinutes) parts.push(`${hours(day.unavailableMinutes)} blocked`);
  return parts.join(' · ') || 'Available';
}
function hours(minutes: number) {
  return `${(minutes / 60).toFixed(minutes % 60 ? 1 : 0)}h`;
}
